import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { Clock, Bell, Send, RefreshCw, CheckCircle, AlertCircle, Play, Calendar, ArrowLeft, Square, Settings, Save } from 'lucide-react';

const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ManageReminders = () => {
  const { token, getAuthHeaders, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [pendingReminders, setPendingReminders] = useState([]);
  const [sentReminders, setSentReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [togglingScheduler, setTogglingScheduler] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Editable settings
  const [editReminderHours, setEditReminderHours] = useState(24);
  const [editCheckInterval, setEditCheckInterval] = useState(15);
  const [settingsChanged, setSettingsChanged] = useState(false);

  useEffect(() => {
    if (!isSuperAdmin()) {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, []);

  // Track if settings have changed
  useEffect(() => {
    if (schedulerStatus) {
      const hoursChanged = editReminderHours !== schedulerStatus.reminder_hours;
      const intervalChanged = editCheckInterval !== schedulerStatus.check_interval_minutes;
      setSettingsChanged(hoursChanged || intervalChanged);
    }
  }, [editReminderHours, editCheckInterval, schedulerStatus]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const [statusRes, pendingRes, sentRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/reminders/status`, {
          headers: getAuthHeaders()
        }),
        axios.get(`${API_BASE_URL}/reminders/pending`, {
          headers: getAuthHeaders()
        }),
        axios.get(`${API_BASE_URL}/reminders/sent?limit=20`, {
          headers: getAuthHeaders()
        })
      ]);
      
      const statusData = statusRes.data.data;
      setSchedulerStatus(statusData);
      setEditReminderHours(statusData.reminder_hours || 24);
      setEditCheckInterval(statusData.check_interval_minutes || 15);
      setPendingReminders(pendingRes.data.data || []);
      setSentReminders(sentRes.data.data || []);
    } catch (err) {
      setError('Failed to load reminder data');
    } finally {
      setLoading(false);
    }
  };

  const handleProcessNow = async () => {
    try {
      setProcessing(true);
      setError('');
      setSuccess('');
      
      await axios.post(`${API_BASE_URL}/reminders/process-now`, {}, {
        headers: getAuthHeaders()
      });
      
      setSuccess('Reminder processing triggered successfully!');
      fetchData(); // Refresh data
    } catch (err) {
      setError('Failed to trigger reminder processing');
    } finally {
      setProcessing(false);
    }
  };

  const handleToggleScheduler = async () => {
    try {
      setTogglingScheduler(true);
      setError('');
      setSuccess('');
      
      const endpoint = schedulerStatus?.running ? 'stop' : 'start';
      await axios.post(`${API_BASE_URL}/reminders/${endpoint}`, {}, {
        headers: getAuthHeaders()
      });
      
      setSuccess(`Scheduler ${schedulerStatus?.running ? 'stopped' : 'started'} successfully!`);
      fetchData(); // Refresh data
    } catch (err) {
      setError(`Failed to ${schedulerStatus?.running ? 'stop' : 'start'} scheduler`);
    } finally {
      setTogglingScheduler(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSavingSettings(true);
      setError('');
      setSuccess('');
      
      await axios.put(`${API_BASE_URL}/reminders/settings`, {
        reminder_hours: editReminderHours,
        check_interval_minutes: editCheckInterval
      }, {
        headers: getAuthHeaders()
      });
      
      setSuccess('Settings saved successfully!');
      setSettingsChanged(false);
      fetchData(); // Refresh data
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to save settings';
      setError(message);
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSendManual = async (appointmentId) => {
    try {
      setError('');
      setSuccess('');
      
      await axios.post(`${API_BASE_URL}/reminders/send-manual`, 
        { appointment_id: appointmentId },
        { headers: getAuthHeaders() }
      );
      
      setSuccess('Reminder sent successfully!');
      fetchData(); // Refresh data
    } catch (err) {
      setError('Failed to send reminder');
    }
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return 'N/A';
    try {
      const date = new Date(isoString);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return isoString;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-6">
          {/* Back to Dashboard */}
          <button
            onClick={() => navigate('/dashboard')}
            className="mb-6 flex items-center text-gray-600 hover:text-gray-800 transition-colors"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Dashboard
          </button>

          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 flex items-center">
                <Bell className="w-8 h-8 mr-3 text-yellow-500" />
                Reminder Scheduler
              </h1>
              <p className="text-gray-600 mt-2">
                Automated booking reminders for upcoming appointments
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={fetchData}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors flex items-center"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </button>
              <button
                onClick={handleProcessNow}
                disabled={processing || !schedulerStatus?.running}
                className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-md transition-colors flex items-center disabled:bg-yellow-300 disabled:cursor-not-allowed"
                title={!schedulerStatus?.running ? "Start scheduler first" : "Process reminders now"}
              >
                {processing ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    Process Now
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Status Messages */}
          {error && (
            <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-md flex items-center">
              <AlertCircle className="w-5 h-5 mr-2" />
              {error}
            </div>
          )}
          {success && (
            <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-md flex items-center">
              <CheckCircle className="w-5 h-5 mr-2" />
              {success}
            </div>
          )}

          {/* Scheduler Status & Settings Card */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                <Settings className="w-5 h-5 mr-2 text-blue-500" />
                Scheduler Settings
              </h2>
              {/* Start/Stop Button */}
              <button
                onClick={handleToggleScheduler}
                disabled={togglingScheduler}
                className={`px-4 py-2 rounded-md transition-colors flex items-center font-semibold ${
                  schedulerStatus?.running 
                    ? 'bg-red-500 hover:bg-red-600 text-white' 
                    : 'bg-green-500 hover:bg-green-600 text-white'
                } disabled:opacity-50`}
                data-testid="toggle-scheduler-btn"
              >
                {togglingScheduler ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    {schedulerStatus?.running ? 'Stopping...' : 'Starting...'}
                  </>
                ) : schedulerStatus?.running ? (
                  <>
                    <Square className="w-4 h-4 mr-2" />
                    Stop Scheduler
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    Start Scheduler
                  </>
                )}
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Status */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-600 mb-1">Status</div>
                <div className={`text-lg font-semibold flex items-center ${
                  schedulerStatus?.running ? 'text-green-600' : 'text-red-600'
                }`}>
                  <span className={`w-3 h-3 rounded-full mr-2 ${
                    schedulerStatus?.running ? 'bg-green-500 animate-pulse' : 'bg-red-500'
                  }`}></span>
                  {schedulerStatus?.running ? 'Running' : 'Stopped'}
                </div>
              </div>
              
              {/* Reminder Window - Editable */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-600 mb-1">Reminder Window</div>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="1"
                    max="168"
                    value={editReminderHours}
                    onChange={(e) => setEditReminderHours(parseInt(e.target.value) || 1)}
                    className="w-20 px-2 py-1 border border-gray-300 rounded-md text-lg font-semibold text-gray-800 focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                    data-testid="reminder-hours-input"
                  />
                  <span className="ml-2 text-gray-600">hours before</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">Range: 1-168 hours</p>
              </div>
              
              {/* Check Interval - Editable */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-600 mb-1">Check Interval</div>
                <div className="flex items-center">
                  <span className="text-gray-600 mr-2">Every</span>
                  <input
                    type="number"
                    min="1"
                    max="60"
                    value={editCheckInterval}
                    onChange={(e) => setEditCheckInterval(parseInt(e.target.value) || 1)}
                    className="w-20 px-2 py-1 border border-gray-300 rounded-md text-lg font-semibold text-gray-800 focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                    data-testid="check-interval-input"
                  />
                  <span className="ml-2 text-gray-600">minutes</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">Range: 1-60 minutes</p>
              </div>
            </div>
            
            {/* Save Settings Button */}
            {settingsChanged && (
              <div className="mt-4 flex justify-end">
                <button
                  onClick={handleSaveSettings}
                  disabled={savingSettings}
                  className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md transition-colors flex items-center font-semibold disabled:opacity-50"
                  data-testid="save-settings-btn"
                >
                  {savingSettings ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Settings
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Pending Reminders */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                <Clock className="w-5 h-5 mr-2 text-orange-500" />
                Pending Reminders
                <span className="ml-auto bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm">
                  {pendingReminders.length}
                </span>
              </h2>
              
              {pendingReminders.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Bell className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No pending reminders</p>
                  <p className="text-sm">All upcoming appointments have been notified</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {pendingReminders.map((reminder) => (
                    <div 
                      key={reminder.id}
                      className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-semibold text-gray-800">{reminder.customer_name}</div>
                          <div className="text-sm text-gray-600">{reminder.customer_email}</div>
                          <div className="text-sm text-gray-500 mt-1">
                            <Calendar className="w-4 h-4 inline mr-1" />
                            {formatDateTime(reminder.start_time)}
                          </div>
                          <div className="text-sm text-gray-500">
                            {reminder.service_name} at {reminder.calendar_name}
                          </div>
                        </div>
                        <button
                          onClick={() => handleSendManual(reminder.id)}
                          className="px-3 py-1 bg-yellow-500 hover:bg-yellow-600 text-white text-sm rounded-md transition-colors flex items-center"
                        >
                          <Send className="w-3 h-3 mr-1" />
                          Send Now
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Sent Reminders */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                <CheckCircle className="w-5 h-5 mr-2 text-green-500" />
                Recently Sent
                <span className="ml-auto bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm">
                  {sentReminders.length}
                </span>
              </h2>
              
              {sentReminders.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Send className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No reminders sent yet</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {sentReminders.map((reminder) => (
                    <div 
                      key={reminder.id}
                      className="border border-gray-200 rounded-lg p-4 bg-green-50"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-semibold text-gray-800">{reminder.customer_name}</div>
                          <div className="text-sm text-gray-600">{reminder.customer_email}</div>
                          <div className="text-sm text-gray-500 mt-1">
                            <Calendar className="w-4 h-4 inline mr-1" />
                            Appointment: {formatDateTime(reminder.start_time)}
                          </div>
                          <div className="text-sm text-gray-500">
                            {reminder.service_name}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-green-600 font-medium flex items-center">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Sent
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {formatDateTime(reminder.reminder_sent_at)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Info Box */}
          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-blue-800 mb-2">How it works</h3>
            <ul className="text-sm text-blue-700 space-y-2">
              <li>• The scheduler automatically checks for appointments at the configured interval</li>
              <li>• Reminders are sent within the configured window before the appointment time</li>
              <li>• Each appointment only receives one reminder (tracked to avoid duplicates)</li>
              <li>• Use "Process Now" to manually trigger the reminder check cycle</li>
              <li>• Reminders use the "Booking Reminder" notification template</li>
              <li>• Changes to settings are applied immediately when saved</li>
            </ul>
          </div>
          
          {/* Plan Restrictions Note */}
          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-amber-800 mb-2">Plan-Based Restrictions</h3>
            <ul className="text-sm text-amber-700 space-y-2">
              <li>• <strong>Email Reminders:</strong> Only sent if Calendar Admin's plan includes "Email Reminders" feature</li>
              <li>• <strong>SMS Reminders:</strong> Only sent if Calendar Admin's plan includes "SMS Reminders" feature</li>
              <li>• Appointments from admins without these features will be marked as "plan restricted" and skipped</li>
              <li>• Check the <strong>Manage Plans</strong> page to configure notification features per plan</li>
            </ul>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default ManageReminders;
